import { Schema } from "effect";
import type { DomainEvent } from "./DomainEvent";

/**
 * Domain event published when an invoice draft is requested from an order.
 * This event represents the start of the invoice creation process.
 * 
 * Business rules captured:
 * - Order exists and is valid for invoice generation
 * - Draft contains order data snapshot at request time
 * - No validation has occurred yet - this is just a request
 */
export interface InvoiceDraftRequested extends DomainEvent {
  readonly type: "InvoiceDraftRequested";
  readonly orderId: string;
  readonly requestedBy: string;
  readonly requestedAt: Date;
  readonly orderSnapshot: {
    readonly customerInfo: Record<string, unknown>;
    readonly items: Array<Record<string, unknown>>;
    readonly financials: Record<string, unknown>;
  };
}

/**
 * Schema for InvoiceDraftRequested domain event
 */
export const InvoiceDraftRequestedSchema = Schema.Struct({
  // Base domain event fields
  type: Schema.Literal("InvoiceDraftRequested"),
  eventId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Event ID must be a valid UUID",
      }
    )
  ),
  occurredOn: Schema.DateFromSelf,
  version: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1, {
      message: () => "Event version must be a positive integer",
    })
  ),
  aggregateType: Schema.String.pipe(Schema.nonEmptyString()),
  aggregateId: Schema.String.pipe(Schema.nonEmptyString()),
  correlationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  causationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  
  // InvoiceDraftRequested specific fields
  orderId: Schema.String.pipe(Schema.nonEmptyString()),
  requestedBy: Schema.String.pipe(Schema.nonEmptyString()),
  requestedAt: Schema.DateFromSelf,
  orderSnapshot: Schema.Struct({
    customerInfo: Schema.Record({
      key: Schema.String,
      value: Schema.Unknown,
    }),
    items: Schema.Array(
      Schema.Record({
        key: Schema.String,
        value: Schema.Unknown,
      })
    ),
    financials: Schema.Record({
      key: Schema.String,
      value: Schema.Unknown,
    }),
  }),
});

/**
 * Schema for InvoiceCreationFailed domain event
 */
export const InvoiceCreationFailedSchema = Schema.Struct({
  // Base domain event fields
  type: Schema.Literal("InvoiceCreationFailed"),
  eventId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Event ID must be a valid UUID",
      }
    )
  ),
  occurredOn: Schema.DateFromSelf,
  version: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1, {
      message: () => "Event version must be a positive integer",
    })
  ),
  aggregateType: Schema.String.pipe(Schema.nonEmptyString()),
  aggregateId: Schema.String.pipe(Schema.nonEmptyString()),
  correlationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  causationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  
  // InvoiceCreationFailed specific fields
  orderId: Schema.String.pipe(Schema.nonEmptyString()),
  attemptedInvoiceNumber: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  failureReason: Schema.String.pipe(Schema.nonEmptyString()),
  failureCategory: Schema.Literal("validation", "technical", "business"),
  retryable: Schema.Boolean,
  attemptedBy: Schema.String.pipe(Schema.nonEmptyString()),
  errorDetails: Schema.optional(
    Schema.Record({
      key: Schema.String,
      value: Schema.Unknown,
    })
  ),
});

/**
 * Type aliases for the events
 */
export type InvoiceDraftRequestedType = Schema.Schema.Type<typeof InvoiceDraftRequestedSchema>;
export type InvoiceCreationFailedType = Schema.Schema.Type<typeof InvoiceCreationFailedSchema>;
